import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaCrown, FaCalendar, FaCreditCard } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { ApiKeysPanel } from '../components/account/ApiKeysPanel';
import { TradingAuthorityPanel } from '../components/account/TradingAuthorityPanel';
import { MembershipBadge } from '../components/membership/MembershipBadge';
import { PaymentHistory } from '../components/payment/PaymentHistory';
import { UpgradeModal } from '../components/membership/UpgradeModal';
import { formatLocalDate } from '../utils/formatters';
import { normalizeMembershipPayload } from '../utils/membership';

export default function AccountPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [membership, setMembership] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [emailValue, setEmailValue] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [emailTicketId, setEmailTicketId] = useState(null);
  const [emailStep, setEmailStep] = useState('idle'); // idle | sending | sent | verifying
  const [emailError, setEmailError] = useState(null);
  const [emailNotice, setEmailNotice] = useState(null);
  const [editingEmail, setEditingEmail] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/account' } });
      return;
    }
    loadMembership();
  }, [isAuthenticated, navigate]);

  const loadMembership = async () => {
    try {
      const data = await api.getMe();
      if (data.code === 'SUCCESS') {
        const normalizedMembership = normalizeMembershipPayload(data.data);
        setMembership(normalizedMembership);
        setEditingEmail(!normalizedMembership?.profile?.email);
      }
    } catch (err) {
      console.error('Failed to load membership:', err);
    }
    setLoading(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return t('membership.forever');
    return formatLocalDate(
      dateStr,
      {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      },
      i18n.language
    );
  };

  const handleEmailSendCode = async () => {
    if (!emailValue) {
      setEmailError(t('auth.emailRequired'));
      return;
    }
    setEmailStep('sending');
    setEmailError(null);
    setEmailNotice(null);
    try {
      const data = await api.requestEmailBindCode(emailValue, i18n.language);
      if (data.code !== 'SUCCESS') {
        throw new Error(data.msg || t('account.emailBindFailed'));
      }
      setEmailTicketId(data.data.ticket_id);
      setEmailStep('sent');
      const masked = data.data.masked_email || emailValue;
      setEmailNotice(t('account.emailCodeSent', { email: masked }));
    } catch (err) {
      setEmailStep('idle');
      setEmailError(err.message || t('account.emailBindFailed'));
    }
  };

  const handleEmailBind = async () => {
    if (!emailTicketId) {
      setEmailError(t('account.emailBindFailed'));
      return;
    }
    if (!emailCode) {
      setEmailError(t('auth.codeRequired'));
      return;
    }
    setEmailStep('verifying');
    setEmailError(null);
    setEmailNotice(null);
    try {
      const data = await api.bindEmail(emailValue, emailTicketId, emailCode);
      if (data.code !== 'SUCCESS') {
        throw new Error(data.msg || t('account.emailBindFailed'));
      }
      setMembership((prev) => ({
        ...(prev || {}),
        profile: { ...(prev?.profile || {}), email: data.data.email },
      }));
      setEmailStep('idle');
      setEmailTicketId(null);
      setEmailCode('');
      setEmailValue('');
      setEditingEmail(false);
      setEmailNotice(t('account.emailBindSuccess'));
    } catch (err) {
      setEmailStep('sent');
      setEmailError(err.message || t('account.emailBindFailed'));
    }
  };

  const resetEmailForm = () => {
    setEmailValue('');
    setEmailCode('');
    setEmailTicketId(null);
    setEmailStep('idle');
    setEmailError(null);
    setEmailNotice(null);
  };

  const hasEmail = Boolean(membership?.profile?.email);
  const membershipLabel =
    membership?.is_expired
      ? t('membership.expired')
      : membership?.tier_info?.display_name ||
        t(`membership.${membership?.tier_name || 'free'}`);
  const membershipExpiryLabel = membership?.is_expired
    ? t('membership.expiredAt', { date: formatDate(membership?.expires_at) })
    : formatDate(membership?.expires_at);

  if (loading) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-accent-strong"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface">
      <div className="p-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
        >
          <FaArrowLeft />
          {t('common.goBack')}
        </button>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-white mb-8">{t('account.title')}</h1>

        <div className="bg-surface-highlight/50 rounded-2xl p-6 mb-8 border border-glass-border">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <FaCrown className="text-accent-warm" />
              {t('account.membershipInfo')}
            </h2>
            <MembershipBadge
              tier={membership?.tier_name || 'free'}
              size="lg"
              expired={Boolean(membership?.is_expired)}
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="text-gray-400 text-sm mb-1">{t('account.currentTier')}</div>
              <div className={`text-lg font-medium ${membership?.is_expired ? 'text-rose-200' : 'text-white'}`}>
                {membershipLabel}
              </div>
            </div>

            <div>
              <div className="text-gray-400 text-sm mb-1 flex items-center gap-1">
                <FaCalendar size={12} />
                {t('membership.expiresAt')}
              </div>
              <div className={`text-lg font-medium ${membership?.is_expired ? 'text-rose-300' : 'text-white'}`}>
                {membershipExpiryLabel}
              </div>
            </div>
          </div>

          {membership?.tier_info?.features && (
            <div className="mt-6 pt-6 border-t border-glass-border">
              <div className="text-gray-400 text-sm mb-3">{t('membership.features')}</div>
              <div className="flex flex-wrap gap-2">
                {membership.tier_info.features.map((feature, idx) => (
                  <span
                    key={idx}
                    className="bg-surface-highlight/60 text-gray-300 text-sm px-3 py-1 rounded-full"
                  >
                    {feature}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* 升级按钮 */}
          {membership?.tier_name !== 'pro' && (
            <button
              onClick={() => setShowUpgradeModal(true)}
              className="mt-6 w-full bg-gradient-to-r from-accent-strong to-accent text-white py-3 rounded-lg font-medium hover:from-accent hover:to-accent-soft transition-colors"
            >
              {t('membership.upgrade')}
            </button>
          )}
        </div>

        <div className="bg-surface-highlight/50 rounded-2xl p-6 mb-8 border border-glass-border">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h2 className="text-xl font-bold text-white">{t('account.backupEmailTitle')}</h2>
              <p className="text-sm text-slate-400 mt-1">{t('account.backupEmailDesc')}</p>
            </div>
            {hasEmail && !editingEmail && (
              <span className="px-2 py-1 text-xs rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                {t('account.emailBoundBadge')}
              </span>
            )}
          </div>

          {emailNotice && (
            <div className="mb-3 text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2">
              {emailNotice}
            </div>
          )}

          {emailError && (
            <div className="mb-3 text-xs text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2">
              {emailError}
            </div>
          )}

          {hasEmail && !editingEmail ? (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <div className="text-xs text-gray-400">{t('account.emailBoundLabel')}</div>
                <div className="text-white text-sm">{membership?.profile?.email}</div>
              </div>
              <button
                onClick={() => {
                  resetEmailForm();
                  setEditingEmail(true);
                }}
                className="px-4 py-2 rounded-lg bg-surface-highlight hover:bg-surface text-white text-sm border border-glass-border transition-colors"
              >
                {t('account.changeEmail')}
              </button>
            </div>
          ) : (
            <div className="grid gap-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1">{t('auth.emailLabel')}</label>
                <input
                  value={emailValue}
                  onChange={(e) => setEmailValue(e.target.value)}
                  placeholder={t('auth.emailPlaceholder')}
                  className="w-full bg-surface-highlight/50 border border-glass-border rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-accent"
                />
              </div>

              {emailStep === 'sent' && (
                <div>
                  <label className="block text-xs text-gray-400 mb-1">{t('auth.codeLabel')}</label>
                  <input
                    value={emailCode}
                    onChange={(e) => setEmailCode(e.target.value)}
                    placeholder={t('auth.codePlaceholder')}
                    className="w-full bg-surface-highlight/50 border border-glass-border rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-accent"
                  />
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                {emailStep !== 'sent' ? (
                  <button
                    onClick={handleEmailSendCode}
                    disabled={emailStep === 'sending'}
                    className="px-4 py-2 rounded-lg bg-surface-highlight hover:bg-surface text-white text-sm border border-glass-border transition-colors disabled:opacity-60"
                  >
                    {emailStep === 'sending' ? t('auth.sendingCode') : t('auth.sendCode')}
                  </button>
                ) : (
                  <>
                    <button
                      onClick={handleEmailBind}
                      disabled={emailStep === 'verifying'}
                      className="px-4 py-2 rounded-lg bg-accent-strong hover:bg-accent text-white text-sm border border-accent/40 transition-colors disabled:opacity-60"
                    >
                      {emailStep === 'verifying' ? t('auth.verifying') : t('auth.verifyCode')}
                    </button>
                    <button
                      onClick={handleEmailSendCode}
                      className="px-4 py-2 rounded-lg border border-glass-border text-slate-300 text-sm hover:text-white transition-colors"
                    >
                      {t('auth.resendCode')}
                    </button>
                  </>
                )}

                {hasEmail && (
                  <button
                    onClick={() => {
                      resetEmailForm();
                      setEditingEmail(false);
                    }}
                    className="px-4 py-2 rounded-lg border border-glass-border text-slate-400 text-sm hover:text-white transition-colors"
                  >
                    {t('common.cancel')}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="mb-8">
          <ApiKeysPanel active />
        </div>

        <div className="mb-8">
          <TradingAuthorityPanel active />
        </div>

        <div className="bg-surface-highlight/50 rounded-2xl p-6 border border-glass-border">
          <h2 className="text-xl font-bold text-white flex items-center gap-2 mb-6">
            <FaCreditCard className="text-accent" />
            {t('payment.history')}
          </h2>
          <PaymentHistory />
        </div>
      </div>

      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        currentTier={membership?.tier_name || 'free'}
      />
    </div>
  );
}
